import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Calendar, MessageSquare, ArrowLeft, RefreshCw, Trash2, StickyNote, X, Check, Edit3, Mail, UserCheck, UserX, Download } from 'lucide-react';

const AdminPage = ({ onBack }) => {
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState({ total_families: 0, total_attendees: 0, attending_count: 0, not_attending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [rsvpRes, statsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsvp`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsvp/stats`)
      ]);

      if (!rsvpRes.ok || !statsRes.ok) throw new Error('Failed to fetch data');

      const rsvpData = await rsvpRes.json();
      const statsData = await statsRes.json();

      setRsvps(rsvpData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load RSVPs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const downloadCSV = () => {
    // CSV headers
    const headers = ['Primary Guest', 'Email', 'Attending', 'Additional Guests', 'Total Guests', 'Notes', 'Admin Note', 'Language', 'Submitted Date'];
    
    // Convert RSVPs to CSV rows
    const rows = rsvps.map(rsvp => {
      const additionalGuests = rsvp.guest_names?.join('; ') || '';
      const totalGuests = rsvp.attending ? 1 + (rsvp.guest_names?.length || 0) : 0;
      const submittedDate = new Date(rsvp.created_at).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
      
      return [
        `"${rsvp.primary_guest_name || ''}"`,
        `"${rsvp.email || ''}"`,
        rsvp.attending ? 'Yes' : 'No',
        `"${additionalGuests}"`,
        totalGuests,
        `"${(rsvp.notes || '').replace(/"/g, '""')}"`,
        `"${(rsvp.admin_note || '').replace(/"/g, '""')}"`,
        rsvp.language === 'bn' ? 'Bangla' : 'English',
        submittedDate
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `wedding-rsvp-list-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (rsvpId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsvp/${rsvpId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete RSVP');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError('Failed to delete RSVP. Please try again.');
    }
  };

  const handleSaveNote = async (rsvpId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsvp/${rsvpId}/note`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_note: noteText || null }),
      });
      if (!response.ok) throw new Error('Failed to save note');
      setEditingNote(null);
      setNoteText('');
      fetchData();
    } catch (err) {
      setError('Failed to save note. Please try again.');
    }
  };

  const startEditingNote = (rsvp) => {
    setEditingNote(rsvp.id);
    setNoteText(rsvp.admin_note || '');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F0] to-[#F5E6D3] py-6 sm:py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#800020] hover:text-[#600018] transition-colors min-h-[44px]"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium text-sm sm:text-base">Back to Site</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={downloadCSV}
              disabled={loading || rsvps.length === 0}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] bg-[#D4AF37] text-[#2D2424] rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50 text-sm sm:text-base font-medium"
              data-testid="admin-download-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download CSV</span>
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] bg-[#800020] text-white rounded-lg hover:bg-[#600018] transition-colors disabled:opacity-50 text-sm sm:text-base"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Title */}
        <motion.div className="text-center mb-8 sm:mb-10" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-[#800020] mb-2">RSVP Responses</h1>
          <div className="w-24 h-0.5 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
            </div>
            <p className="text-2xl sm:text-4xl font-heading font-bold text-[#800020]">{stats.total_families}</p>
            <p className="text-xs sm:text-sm text-[#2D2424]/60 mt-1">Responses</p>
          </div>
          <div className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-[#800020]/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#800020]" />
            </div>
            <p className="text-2xl sm:text-4xl font-heading font-bold text-[#800020]">{stats.total_attendees}</p>
            <p className="text-xs sm:text-sm text-[#2D2424]/60 mt-1">Total Guests</p>
          </div>
          <div className="bg-white/80 backdrop-blur-sm border border-green-200 rounded-xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-green-100 rounded-full flex items-center justify-center">
              <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <p className="text-2xl sm:text-4xl font-heading font-bold text-green-600">{stats.attending_count}</p>
            <p className="text-xs sm:text-sm text-[#2D2424]/60 mt-1">Attending</p>
          </div>
          <div className="bg-white/80 backdrop-blur-sm border border-red-200 rounded-xl p-4 sm:p-6 text-center shadow-lg">
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-red-100 rounded-full flex items-center justify-center">
              <UserX className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
            </div>
            <p className="text-2xl sm:text-4xl font-heading font-bold text-red-500">{stats.not_attending_count}</p>
            <p className="text-xs sm:text-sm text-[#2D2424]/60 mt-1">Not Attending</p>
          </div>
        </motion.div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-center text-red-600 text-sm sm:text-base">
            {error}
            <button onClick={() => setError('')} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#2D2424]/60">Loading RSVPs...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && rsvps.length === 0 && (
          <motion.div
            className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-xl p-8 sm:p-12 text-center shadow-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Users className="w-16 h-16 mx-auto text-[#D4AF37]/50 mb-4" />
            <h3 className="font-heading text-xl sm:text-2xl text-[#800020] mb-2">No RSVPs Yet</h3>
            <p className="text-[#2D2424]/60 text-sm sm:text-base">RSVPs will appear here as guests respond.</p>
          </motion.div>
        )}

        {/* RSVP List */}
        {!loading && !error && rsvps.length > 0 && (
          <motion.div className="space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            {rsvps.map((rsvp, index) => (
              <motion.div
                key={rsvp.id}
                className={`bg-white/80 backdrop-blur-sm border rounded-xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-shadow ${
                  rsvp.attending ? 'border-green-200' : 'border-red-200'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`rsvp-item-${rsvp.id}`}
              >
                <div className="flex flex-col gap-3">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-lg sm:text-xl text-[#800020] font-semibold truncate">
                          {rsvp.primary_guest_name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          rsvp.attending ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {rsvp.attending ? 'Attending' : 'Not Attending'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs sm:text-sm text-[#2D2424]/60 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="truncate max-w-[150px] sm:max-w-none">{rsvp.email}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                          {formatDate(rsvp.created_at)}
                        </span>
                      </div>
                    </div>
                    {rsvp.attending && (
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg">
                        {1 + (rsvp.guest_names?.length || 0)}
                      </div>
                    )}
                  </div>

                  {/* Additional Guests */}
                  {rsvp.attending && rsvp.guest_names && rsvp.guest_names.length > 0 && (
                    <div className="p-3 bg-[#F5E6D3]/50 rounded-lg">
                      <p className="text-xs text-[#800020] font-medium mb-1">Additional Guests:</p>
                      <div className="flex flex-wrap gap-2">
                        {rsvp.guest_names.map((name, i) => (
                          <span key={`${rsvp.id}-guest-${i}`} className="px-2 py-1 bg-white rounded text-xs sm:text-sm text-[#2D2424]">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes from Guest */}
                  {rsvp.notes && (
                    <div className="p-3 bg-[#F5E6D3]/50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <p className="text-xs sm:text-sm text-[#2D2424]/80 italic">"{rsvp.notes}"</p>
                      </div>
                    </div>
                  )}

                  {/* Admin Note */}
                  {editingNote === rsvp.id ? (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-2">
                        <StickyNote className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add your note here..."
                            className="w-full p-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                            rows={2}
                            maxLength={500}
                            autoFocus
                          />
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => handleSaveNote(rsvp.id)}
                              className="flex items-center gap-1 px-3 py-1.5 min-h-[36px] bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                            >
                              <Check className="w-4 h-4" /> Save
                            </button>
                            <button
                              onClick={() => { setEditingNote(null); setNoteText(''); }}
                              className="flex items-center gap-1 px-3 py-1.5 min-h-[36px] bg-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              <X className="w-4 h-4" /> Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : rsvp.admin_note ? (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-2">
                        <StickyNote className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p className="flex-1 text-sm text-blue-700">{rsvp.admin_note}</p>
                        <button onClick={() => startEditingNote(rsvp)} className="text-blue-500 hover:text-blue-700 transition-colors" title="Edit note">
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-[#E6C288]/50">
                    {editingNote !== rsvp.id && !rsvp.admin_note && (
                      <button
                        onClick={() => startEditingNote(rsvp)}
                        className="flex items-center gap-1.5 px-3 py-1.5 min-h-[40px] text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        data-testid={`add-note-btn-${rsvp.id}`}
                      >
                        <StickyNote className="w-4 h-4" /> Add Note
                      </button>
                    )}
                    {deleteConfirm === rsvp.id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-red-600">Delete this RSVP?</span>
                        <button
                          onClick={() => handleDelete(rsvp.id)}
                          className="flex items-center gap-1 px-3 py-1.5 min-h-[40px] bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
                          data-testid={`confirm-delete-btn-${rsvp.id}`}
                        >
                          <Check className="w-4 h-4" /> Yes
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="flex items-center gap-1 px-3 py-1.5 min-h-[40px] bg-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          <X className="w-4 h-4" /> No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(rsvp.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 min-h-[40px] text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-btn-${rsvp.id}`}
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 sm:mt-12 text-xs sm:text-sm text-[#2D2424]/40">
          <p>Anica & Tamjid's Wedding · August 16, 2026</p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
